import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Plus, Trash2, Key, Copy, Check, ExternalLink, X,
  Layers, BarChart3, AlertTriangle, Eye, EyeOff, Info,
  Activity, TrendingUp, Palette, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Platform Admin — human-facing UI for the mgmt API v1.
 *
 * Three tabs:
 *   Workspaces   list + create + rename + soft-delete
 *   API Keys     list + create (one-time reveal) + revoke + "Try in Portal"
 *   Usage        daily rollups (workspace × day) with a workspace filter
 *
 * Every mutating call goes through the session-authenticated version of
 * the mgmt API (no Bearer key needed — the current admin's cookie + CSRF
 * pair does the work).
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ── HTTP helpers (session + CSRF) ────────────────────────────────────────

function getCsrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

async function apiCall(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    headers['X-CSRF-Token'] = getCsrfToken();
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const detail = typeof data === 'object' ? data.detail || JSON.stringify(data) : text;
    throw new Error(`${res.status}: ${detail}`);
  }
  return data;
}

// ── Shared bits ─────────────────────────────────────────────────────────

function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="rounded-lg border border-dashed border-border/40 py-10 px-6 text-center">
      <Icon className="w-6 h-6 mx-auto text-muted-foreground/80 mb-2" />
      <div className="text-sm font-medium">{title}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}

// ── Workspaces tab ──────────────────────────────────────────────────────

function WorkspacesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [brandingWs, setBrandingWs] = useState(null); // {workspace_id, name}

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const r = await apiCall('GET', '/api/mgmt/v1/workspaces');
      setRows(r.data || []);
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const doCreate = async () => {
    if (!newName.trim()) return;
    try {
      const body = { name: newName.trim() };
      if (newSlug.trim()) body.slug = newSlug.trim();
      await apiCall('POST', '/api/mgmt/v1/workspaces', body);
      toast.success(`Workspace "${newName}" created`);
      setNewName(''); setNewSlug(''); setCreating(false);
      refresh();
    } catch (e) {
      toast.error(`Create failed: ${e.message}`);
    }
  };

  const doDelete = async (ws) => {
    if (!window.confirm(`Delete workspace "${ws.name}"? This is a soft-delete with 30-day grace.`)) return;
    try {
      await apiCall('DELETE', `/api/mgmt/v1/workspaces/${ws.workspace_id}`);
      toast.success('Deleted');
      refresh();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  return (
    <Section
      title={`Workspaces (${rows.length})`}
      right={
        <button
          onClick={() => setCreating(v => !v)}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-border/40 hover:bg-muted/40"
          data-testid="platform-admin-new-workspace-btn"
        >
          <Plus className="w-3 h-3" /> New workspace
        </button>
      }
    >
      {creating && (
        <div className="rounded-lg border border-border/40 bg-muted/10 p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Name (e.g. Contoso)"
              className="text-xs px-2 py-1.5 rounded border border-border/40 bg-background/60"
              data-testid="platform-admin-workspace-name-input"
            />
            <input
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              placeholder="Slug (auto if blank)"
              className="text-xs px-2 py-1.5 rounded border border-border/40 bg-background/60 font-mono"
              data-testid="platform-admin-workspace-slug-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={doCreate}
              disabled={!newName.trim()}
              className="text-xs px-3 py-1 rounded bg-primary text-primary-foreground disabled:opacity-40"
              data-testid="platform-admin-workspace-create-submit"
            >
              Create
            </button>
            <button
              onClick={() => { setCreating(false); setNewName(''); setNewSlug(''); }}
              className="text-xs px-3 py-1 rounded border border-border/40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Layers}
          title="No workspaces yet"
          hint="Create one to start provisioning users and issuing embed sessions."
        />
      ) : (
        <div className="rounded-lg border border-border/40 overflow-hidden">
          <table className="w-full text-xs" data-testid="platform-admin-workspaces-table">
            <thead className="bg-muted/20 text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">Slug</th>
                <th className="text-left px-3 py-2 font-medium">Workspace ID</th>
                <th className="text-right px-3 py-2 font-medium">AI cap</th>
                <th className="text-right px-3 py-2 font-medium">Inv. cap</th>
                <th className="text-right px-3 py-2 font-medium">Created</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.workspace_id}
                  className="border-t border-border/25 hover:bg-muted/10"
                  data-testid={`workspace-row-${r.workspace_id}`}
                >
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.slug}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">{r.workspace_id}</td>
                  <td className="px-3 py-2 text-right">${(r.settings?.ai_budget_usd_daily ?? 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">${(r.settings?.investigation_budget_usd_daily ?? 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setBrandingWs(r)}
                        className="text-muted-foreground hover:text-foreground"
                        title="Branding"
                        data-testid={`workspace-branding-${r.workspace_id}`}
                      >
                        <Palette className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => doDelete(r)}
                        className="text-rose-400 hover:text-rose-300"
                        title="Delete"
                        data-testid={`workspace-delete-${r.workspace_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {brandingWs && (
        <BrandingModal
          workspace={brandingWs}
          onClose={() => setBrandingWs(null)}
        />
      )}
    </Section>
  );
}

// ── Branding modal ──────────────────────────────────────────────────────

function BrandingModal({ workspace, onClose }) {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const r = await apiCall('GET', `/api/mgmt/v1/workspaces/${workspace.workspace_id}/branding`);
        if (alive) setBranding(r.branding || {});
      } catch (e) {
        toast.error(`Load branding failed: ${e.message}`);
        if (alive) setBranding({});
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [workspace.workspace_id]);

  const set = (k, v) => setBranding((b) => ({ ...(b || {}), [k]: v }));

  const save = async () => {
    // Send only the whitelisted keys we edit here; empty strings coerce
    // to explicit null (clears the field).
    const payload = {};
    const editable = [
      'product_name', 'logo_url', 'favicon_url',
      'primary_color', 'accent_color', 'background_color', 'text_color',
    ];
    for (const k of editable) {
      const v = branding?.[k];
      if (v === undefined) continue;
      payload[k] = v === '' ? null : v;
    }
    try {
      setSaving(true);
      const r = await apiCall(
        'PUT',
        `/api/mgmt/v1/workspaces/${workspace.workspace_id}/branding`,
        payload,
      );
      setBranding(r.branding || {});
      toast.success('Branding saved');
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      data-testid="branding-modal-backdrop"
    >
      <div
        className="w-full max-w-lg rounded-lg border border-border/40 bg-background p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="branding-modal"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Branding</div>
            <div className="text-sm font-semibold">{workspace.name}</div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            data-testid="branding-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : (
          <>
            <BrandingField
              label="Product name"
              placeholder={workspace.name}
              value={branding?.product_name || ''}
              onChange={(v) => set('product_name', v)}
              testid="branding-product-name"
              hint="Shown in the header of every embedded surface."
            />
            <BrandingField
              label="Logo URL"
              placeholder="https://cdn.example.com/logo.svg"
              value={branding?.logo_url || ''}
              onChange={(v) => set('logo_url', v)}
              testid="branding-logo-url"
              hint="Must be an https:// URL. Renders at 24-32px tall."
            />
            <div className="grid grid-cols-2 gap-3">
              <BrandingColor
                label="Primary color"
                value={branding?.primary_color || ''}
                onChange={(v) => set('primary_color', v)}
                testid="branding-primary-color"
              />
              <BrandingColor
                label="Accent color"
                value={branding?.accent_color || ''}
                onChange={(v) => set('accent_color', v)}
                testid="branding-accent-color"
              />
              <BrandingColor
                label="Background"
                value={branding?.background_color || ''}
                onChange={(v) => set('background_color', v)}
                testid="branding-background-color"
              />
              <BrandingColor
                label="Text"
                value={branding?.text_color || ''}
                onChange={(v) => set('text_color', v)}
                testid="branding-text-color"
              />
            </div>

            <BrandingPreview branding={branding || {}} />

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={onClose}
                className="text-xs px-3 py-1 rounded border border-border/40"
                data-testid="branding-modal-cancel"
              >
                Close
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="text-xs px-3 py-1 rounded bg-primary text-primary-foreground disabled:opacity-40"
                data-testid="branding-modal-save"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function BrandingField({ label, value, onChange, placeholder, hint, testid }) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-xs px-2 py-1.5 rounded border border-border/40 bg-background/60"
        data-testid={testid}
      />
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function BrandingColor({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 w-8 rounded border border-border/40 bg-background/60 p-0.5 cursor-pointer"
          data-testid={`${testid}-picker`}
        />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#RRGGBB"
          className="flex-1 text-xs px-2 py-1.5 rounded border border-border/40 bg-background/60 font-mono"
          data-testid={testid}
        />
      </div>
    </div>
  );
}

function BrandingPreview({ branding }) {
  const bg = branding.background_color || '#0b0b0f';
  const fg = branding.text_color || '#e5e7eb';
  const primary = branding.primary_color || '#4f46e5';
  return (
    <div
      className="rounded-lg border border-border/40 p-3 text-xs"
      style={{ backgroundColor: bg, color: fg }}
      data-testid="branding-preview"
    >
      <div className="flex items-center gap-2 mb-2">
        {branding.logo_url ? (
          <img src={branding.logo_url} alt="" className="h-5 w-auto max-w-[100px] object-contain" />
        ) : (
          <div className="h-5 w-5 rounded" style={{ backgroundColor: primary }} />
        )}
        <span className="font-medium">{branding.product_name || 'Analytics'}</span>
      </div>
      <div className="flex items-center gap-2">
        <span
          className="text-[10px] px-2 py-1 rounded-full text-white"
          style={{ backgroundColor: primary }}
        >
          Primary
        </span>
        <span
          className="text-[10px] px-2 py-1 rounded-full text-white"
          style={{ backgroundColor: branding.accent_color || '#22d3ee' }}
        >
          Accent
        </span>
      </div>
    </div>
  );
}

// ── API Keys tab ────────────────────────────────────────────────────────

function ApiKeysTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [env, setEnv] = useState('live');
  const [scopeInput, setScopeInput] = useState('*');
  const [reveal, setReveal] = useState(null); // { key_id, secret_key, name, env }
  const [loginRows, setLoginRows] = useState(null);
  const [failedRows, setFailedRows] = useState(null);
  const [lockedRows, setLockedRows] = useState(null);
  const [unlocking, setUnlocking] = useState('');

  const loadLoginActivity = useCallback(async () => {
    try {
      const r = await apiCall('GET', '/api/mgmt/v1/admin/login-activity?limit=50');
      setLoginRows(r.data || []);
    } catch {
      setLoginRows([]);
    }
    try {
      const f = await apiCall('GET', '/api/mgmt/v1/admin/failed-logins?limit=30');
      setFailedRows(f.data || []);
    } catch {
      setFailedRows([]);
    }
    try {
      const l = await apiCall('GET', '/api/mgmt/v1/admin/locked-accounts');
      setLockedRows(l.data || []);
    } catch {
      setLockedRows([]);
    }
  }, []);

  const unlockAccount = useCallback(async (email) => {
    setUnlocking(email);
    try {
      const r = await apiCall('POST', '/api/mgmt/v1/admin/unlock-account', { email });
      toast.success(`Unlocked ${email}${r.cleared_failed_attempts ? ` · cleared ${r.cleared_failed_attempts} failed attempts` : ''}`);
      await loadLoginActivity();
    } catch (e) {
      toast.error(`Unlock failed: ${e.message}`);
    } finally {
      setUnlocking('');
    }
  }, [loadLoginActivity]);

  useEffect(() => { loadLoginActivity(); }, [loadLoginActivity]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const r = await apiCall('GET', '/api/mgmt/v1/api-keys');
      setRows(r.data || []);
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const doCreate = async () => {
    if (!name.trim()) return;
    try {
      const scopes = scopeInput.trim()
        ? scopeInput.split(',').map(s => s.trim()).filter(Boolean)
        : ['*'];
      const body = { name: name.trim(), env, scopes };
      const r = await apiCall('POST', '/api/mgmt/v1/api-keys', body);
      toast.success(`Key "${name}" created — copy it now, we can't show it again`);
      setReveal(r);
      setName(''); setScopeInput('*'); setCreating(false);
      refresh();
    } catch (e) {
      toast.error(`Create failed: ${e.message}`);
    }
  };

  const doRevoke = async (row) => {
    if (!window.confirm(`Revoke key "${row.name}"? This cannot be undone.`)) return;
    try {
      await apiCall('DELETE', `/api/mgmt/v1/api-keys/${row.key_id}`);
      toast.success('Revoked');
      refresh();
    } catch (e) {
      toast.error(`Revoke failed: ${e.message}`);
    }
  };

  // Surface when a key is stale or nearing its grace-window expiry so admins
  // rotate on time. Returns null when nothing needs attention.
  const rotationHint = (r) => {
    const now = Date.now();
    if (r.expires_at) {
      const exp = new Date(r.expires_at).getTime();
      const hrs = (exp - now) / 3600000;
      if (hrs <= 0) return { label: 'expired', cls: 'bg-rose-500/15 text-rose-300', title: 'This key has passed its expiry.' };
      if (hrs <= 72) return { label: `expires in ${Math.max(1, Math.round(hrs))}h`, cls: 'bg-amber-500/15 text-amber-300', title: `Rotating out — expires ${new Date(r.expires_at).toLocaleString()}.` };
    }
    if (r.rotated_to) return { label: 'rotating out', cls: 'bg-sky-500/15 text-sky-300', title: `Replaced by ${r.rotated_to}.` };
    if (r.created_at) {
      const ageDays = (now - new Date(r.created_at).getTime()) / 86400000;
      if (ageDays >= 90) return { label: `${Math.round(ageDays)}d old`, cls: 'bg-amber-500/10 text-amber-300/90', title: 'This key is over 90 days old — consider rotating it.' };
    }
    return null;
  };

  const doRotate = async (row) => {
    const input = window.prompt(
      `Rotate key "${row.name}"?\n\nA new key is issued now; the old one keeps working for the grace period (hours), then auto-expires. Enter grace hours (0 = kill old key immediately):`,
      '24',
    );
    if (input === null) return;
    const grace = Math.max(0, Math.min(parseInt(input, 10) || 0, 720));
    try {
      const r = await apiCall('POST', `/api/mgmt/v1/api-keys/${row.key_id}/rotate`, { grace_hours: grace });
      setReveal({ ...r.new_key, _rotated: true, _oldExpiry: r.old_key_expires_at, _oldRevoked: r.old_key_revoked });
      toast.success(grace === 0 ? 'Rotated — old key revoked' : `Rotated — old key valid for ${grace}h`);
      refresh();
    } catch (e) {
      toast.error(`Rotate failed: ${e.message}`);
    }
  };

  const [copied, setCopied] = useState(false);
  const copySecret = async () => {
    if (!reveal?.secret_key) return;
    try {
      await navigator.clipboard.writeText(reveal.secret_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (_) { /* clipboard blocked — no-op */ }
  };

  return (
    <>
      <Section
        title={`API Keys (${rows.length})`}
        right={
          <button
            onClick={() => setCreating(v => !v)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-border/40 hover:bg-muted/40"
            data-testid="platform-admin-new-apikey-btn"
          >
            <Plus className="w-3 h-3" /> New key
          </button>
        }
      >
        {creating && (
          <div className="rounded-lg border border-border/40 bg-muted/10 p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name (e.g. Acme host)"
                className="text-xs px-2 py-1.5 rounded border border-border/40 bg-background/60"
                data-testid="platform-admin-apikey-name-input"
              />
              <select
                value={env}
                onChange={(e) => setEnv(e.target.value)}
                className="text-xs px-2 py-1.5 rounded border border-border/40 bg-background/60"
                data-testid="platform-admin-apikey-env-select"
              >
                <option value="live">live (production)</option>
                <option value="test">test (development)</option>
              </select>
            </div>
            <input
              value={scopeInput}
              onChange={(e) => setScopeInput(e.target.value)}
              placeholder="Scopes: * (master) or comma-separated (workspaces:read, sessions:issue, …)"
              className="w-full text-xs px-2 py-1.5 rounded border border-border/40 bg-background/60 font-mono"
              data-testid="platform-admin-apikey-scopes-input"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={doCreate}
                disabled={!name.trim()}
                className="text-xs px-3 py-1 rounded bg-primary text-primary-foreground disabled:opacity-40"
                data-testid="platform-admin-apikey-create-submit"
              >
                Create key
              </button>
              <button
                onClick={() => { setCreating(false); setName(''); setScopeInput('*'); }}
                className="text-xs px-3 py-1 rounded border border-border/40"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Key}
            title="No API keys yet"
            hint="Create one to enable your host application to call the management plane."
          />
        ) : (
          <div className="rounded-lg border border-border/40 overflow-hidden">
            <table className="w-full text-xs" data-testid="platform-admin-apikeys-table">
              <thead className="bg-muted/20 text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Name</th>
                  <th className="text-left px-3 py-2 font-medium">Prefix</th>
                  <th className="text-left px-3 py-2 font-medium">Env</th>
                  <th className="text-left px-3 py-2 font-medium">Scopes</th>
                  <th className="text-left px-3 py-2 font-medium">Last used</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.key_id}
                    className="border-t border-border/25 hover:bg-muted/10"
                    data-testid={`apikey-row-${r.key_id}`}
                  >
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{r.prefix}…</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${r.env === 'live' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
                        {r.env}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] truncate max-w-[240px]">
                      {(r.scopes || []).join(', ')}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {r.last_used_at ? new Date(r.last_used_at).toLocaleString() : 'never'}
                    </td>
                    <td className="px-3 py-2">
                      {r.revoked_at
                        ? <span className="text-rose-300">revoked</span>
                        : <div className="flex items-center gap-1.5">
                            <span className="text-emerald-300">active</span>
                            {rotationHint(r) && (
                              <span
                                className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${rotationHint(r).cls}`}
                                title={rotationHint(r).title}
                                data-testid={`apikey-rotation-hint-${r.key_id}`}
                              >
                                <AlertTriangle className="w-3 h-3" />
                                {rotationHint(r).label}
                              </span>
                            )}
                          </div>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {!r.revoked_at && (
                        <div className="inline-flex items-center gap-3">
                          <button
                            onClick={() => doRotate(r)}
                            className="text-sky-400 hover:text-sky-300"
                            title="Rotate (issue a new key, expire this one after a grace window)"
                            data-testid={`apikey-rotate-${r.key_id}`}
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => doRevoke(r)}
                            className="text-rose-400 hover:text-rose-300"
                            title="Revoke"
                            data-testid={`apikey-revoke-${r.key_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div className="mt-8">
        <Section
          title="Login activity"
          right={
            <button
              onClick={loadLoginActivity}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-border/40 bg-muted/20 text-muted-foreground hover:text-foreground"
              data-testid="login-activity-refresh"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          }
        >
          <p className="text-[11px] text-muted-foreground -mt-1 mb-1">New-location admin sign-ins and account lockouts (from the audit trail)</p>
          {loginRows === null ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : loginRows.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="login-activity-empty">
              No security login events yet. New-location admin sign-ins and lockouts will appear here.
            </p>
          ) : (
            <div className="rounded border border-border/40 divide-y divide-border/30" data-testid="login-activity-list">
              {loginRows.map((r) => {
                const locked = r.action === 'security.account_locked';
                return (
                  <div key={r.log_id} className="px-3 py-2 text-xs flex items-center justify-between gap-2" data-testid="login-activity-row">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[11px] font-semibold ${locked ? 'text-rose-300' : 'text-amber-300'}`}>
                          {locked ? 'Account locked' : 'New-location sign-in'}
                        </span>
                        <span className="font-mono text-[10px] text-muted-foreground truncate">{r.metadata?.email || r.resource_id}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        IP {r.metadata?.ip || 'unknown'} · {r.ts ? new Date(r.ts).toLocaleString() : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-[11px] text-muted-foreground mt-4 mb-1">Currently locked accounts</p>
          {lockedRows === null ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : lockedRows.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="locked-accounts-empty">
              No accounts are locked right now.
            </p>
          ) : (
            <div className="rounded border border-border/40 divide-y divide-border/30" data-testid="locked-accounts-list">
              {lockedRows.map((r, i) => (
                <div key={i} className="px-3 py-2 text-xs flex items-center justify-between gap-2" data-testid="locked-account-row">
                  <div className="min-w-0">
                    <span className="font-mono text-[11px] text-rose-600 dark:text-rose-300 truncate">{r.email}</span>
                    <span className="block text-[10px] text-muted-foreground">
                      Locked until {r.locked_until ? new Date(r.locked_until).toLocaleString() : 'unknown'}
                      {r.lock_reason ? ` · ${r.lock_reason}` : ''}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="h-7 px-3 shrink-0 rounded border border-border/60 text-[11px] font-medium hover:bg-muted/40 disabled:opacity-50"
                    disabled={unlocking === r.email}
                    onClick={() => unlockAccount(r.email)}
                    data-testid={`unlock-account-${r.email}`}
                  >
                    {unlocking === r.email ? 'Unlocking…' : 'Unlock'}
                  </button>
                </div>
              ))}
            </div>
          )}

          <p className="text-[11px] text-muted-foreground mt-4 mb-1">Recent failed sign-ins</p>
          {failedRows === null ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : failedRows.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="failed-logins-empty">
              No recent failed sign-ins for this tenant&rsquo;s users.
            </p>
          ) : (
            <div className="rounded border border-border/40 divide-y divide-border/30" data-testid="failed-logins-list">
              {failedRows.map((r, i) => (
                <div key={i} className="px-3 py-2 text-xs flex items-center justify-between gap-2" data-testid="failed-login-row">
                  <span className="font-mono text-[10px] text-muted-foreground truncate">{r.email}</span>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    IP {r.ip || 'unknown'} · {r.timestamp ? new Date(r.timestamp).toLocaleString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {reveal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          data-testid="apikey-reveal-modal"
        >
          <div className="max-w-lg w-full bg-background border border-border/50 rounded-lg p-5 shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <h3 className="text-sm font-semibold">Copy your key now</h3>
                </div>
                <p className="text-xs text-muted-foreground">
                  This is the only time we can show the full secret. If you lose it,
                  create a new key and revoke this one.
                </p>
                {reveal._rotated && (
                  <p className="text-[11px] mt-1 text-sky-300" data-testid="apikey-reveal-rotated">
                    {reveal._oldRevoked
                      ? 'Rotated — the previous key was revoked immediately.'
                      : `Rotated — the previous key keeps working until ${reveal._oldExpiry ? new Date(reveal._oldExpiry).toLocaleString() : 'the grace window ends'}.`}
                  </p>
                )}
              </div>
              <button
                onClick={() => setReveal(null)}
                className="text-muted-foreground hover:text-foreground"
                data-testid="apikey-reveal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-4 space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Secret key</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-xs px-3 py-2 rounded bg-black/40 border border-border/40 truncate select-all" data-testid="apikey-reveal-secret">
                  {reveal.secret_key}
                </code>
                <button
                  onClick={copySecret}
                  className="text-xs px-3 py-2 rounded border border-border/40 hover:bg-muted/40 inline-flex items-center gap-1"
                  data-testid="apikey-reveal-copy"
                >
                  {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Prefix: <code>{reveal.prefix}…</code>  ·  Env: {reveal.env}  ·  Scopes: <code className="text-[10px]">{(reveal.scopes || []).join(', ')}</code>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-2">
              <Link
                to={`/developers?key=${encodeURIComponent(reveal.secret_key)}`}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-primary/50 bg-primary/10 hover:bg-primary/20 text-primary"
                data-testid="apikey-reveal-try-in-portal"
              >
                <ExternalLink className="w-3 h-3" /> Try in Developer Portal
              </Link>
              <button
                onClick={() => setReveal(null)}
                className="text-xs px-3 py-1.5 rounded border border-border/40"
              >
                I&apos;ve copied it, close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Usage tab ───────────────────────────────────────────────────────────

function UsageTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [filterWs, setFilterWs] = useState('');

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [wsRes, uRes] = await Promise.all([
        apiCall('GET', '/api/mgmt/v1/workspaces'),
        apiCall(
          'GET',
          `/api/mgmt/v1/usage${filterWs ? `?workspace_id=${encodeURIComponent(filterWs)}` : ''}`,
        ),
      ]);
      setWorkspaces(wsRes.data || []);
      setRows(uRes.data || []);
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [filterWs]);

  useEffect(() => { refresh(); }, [refresh]);

  const wsNameById = useMemo(() => {
    const m = new Map();
    workspaces.forEach(w => m.set(w.workspace_id, w.name));
    return m;
  }, [workspaces]);

  const totals = useMemo(() => {
    const t = { queries: 0, ai_queries: 0, cache_hits: 0, cost: 0, ms: 0 };
    rows.forEach(r => {
      t.queries += r.queries || 0;
      t.ai_queries += r.ai_queries || 0;
      t.cache_hits += r.cache_hits || 0;
      t.cost += r.cost_estimate_usd || 0;
      t.ms += r.ms_total || 0;
    });
    return t;
  }, [rows]);

  return (
    <Section
      title="Daily Usage Rollups"
      right={
        <select
          value={filterWs}
          onChange={(e) => setFilterWs(e.target.value)}
          className="text-xs px-2 py-1 rounded border border-border/40 bg-background/60"
          data-testid="platform-admin-usage-workspace-filter"
        >
          <option value="">All workspaces</option>
          {workspaces.map(w => (
            <option key={w.workspace_id} value={w.workspace_id}>
              {w.name} ({w.slug})
            </option>
          ))}
        </select>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        <StatCard label="Queries"     value={totals.queries.toLocaleString()} />
        <StatCard label="AI queries"  value={totals.ai_queries.toLocaleString()} />
        <StatCard label="Cache hits"  value={totals.cache_hits.toLocaleString()} />
        <StatCard label="Est. cost"   value={`$${totals.cost.toFixed(4)}`} />
        <StatCard label="Elapsed"     value={`${(totals.ms / 1000).toFixed(1)}s`} />
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title="No usage data yet"
          hint="Query events land here once workspaces start running governed queries."
        />
      ) : (
        <div className="rounded-lg border border-border/40 overflow-hidden">
          <table className="w-full text-xs" data-testid="platform-admin-usage-table">
            <thead className="bg-muted/20 text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Day</th>
                <th className="text-left px-3 py-2 font-medium">Workspace</th>
                <th className="text-right px-3 py-2 font-medium">Queries</th>
                <th className="text-right px-3 py-2 font-medium">AI</th>
                <th className="text-right px-3 py-2 font-medium">Cache</th>
                <th className="text-right px-3 py-2 font-medium">Unique users</th>
                <th className="text-right px-3 py-2 font-medium">Elapsed</th>
                <th className="text-right px-3 py-2 font-medium">Est. cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.workspace_id}-${r.day}-${i}`} className="border-t border-border/25 hover:bg-muted/10">
                  <td className="px-3 py-2 font-mono text-[11px]">{r.day}</td>
                  <td className="px-3 py-2">{wsNameById.get(r.workspace_id) || r.workspace_id}</td>
                  <td className="px-3 py-2 text-right">{r.queries || 0}</td>
                  <td className="px-3 py-2 text-right">{r.ai_queries || 0}</td>
                  <td className="px-3 py-2 text-right">{r.cache_hits || 0}</td>
                  <td className="px-3 py-2 text-right">{r.unique_users || 0}</td>
                  <td className="px-3 py-2 text-right">{((r.ms_total || 0) / 1000).toFixed(2)}s</td>
                  <td className="px-3 py-2 text-right">${(r.cost_estimate_usd || 0).toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-lg border border-border/40 bg-background/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function HealthCard({ icon: Icon, label, value, tone = 'neutral', testid }) {
  const toneClasses = {
    warn: 'border-amber-500/40 bg-amber-500/10',
    neutral: 'border-border/40 bg-background/40',
  }[tone] || 'border-border/40 bg-background/40';
  const iconTone = tone === 'warn' ? 'text-amber-400' : 'text-muted-foreground/70';
  return (
    <div
      className={`rounded-lg border ${toneClasses} px-3 py-2.5`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${iconTone}`} />
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      </div>
      <div className="text-lg font-semibold truncate">{value}</div>
    </div>
  );
}

// ── Page shell ──────────────────────────────────────────────────────────

const TABS = [
  { key: 'workspaces', label: 'Workspaces', icon: Layers, Component: WorkspacesTab },
  { key: 'api-keys', label: 'API Keys', icon: Key, Component: ApiKeysTab },
  { key: 'usage', label: 'Usage', icon: BarChart3, Component: UsageTab },
];

export default function PlatformAdminPage() {
  const [tab, setTab] = useState('workspaces');
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const h = await apiCall('GET', '/api/mgmt/v1/health');
        if (alive) setHealth(h);
      } catch (_) {
        /* Health panel is best-effort — silent failure is fine. */
      } finally {
        if (alive) setHealthLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tab]); // refresh when the user switches tabs (cheap, common in admin UIs)

  const Active = useMemo(
    () => TABS.find(t => t.key === tab)?.Component || WorkspacesTab,
    [tab],
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="platform-admin-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Platform Admin</div>
          <h1 className="text-3xl font-semibold flex items-center gap-2">
            <Shield className="w-6 h-6 text-amber-400" />
            Embed Management
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Provision client workspaces, mint host-facing API keys, and inspect
            embed usage. Everything you configure here maps 1:1 to the
            <Link to="/developers" className="text-primary hover:underline mx-1">Management API v1</Link>
            so hosts can automate the same flows.
          </p>
        </div>
        <div className="flex flex-col gap-1 items-end shrink-0">
          <Link
            to="/developers"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            data-testid="platform-admin-open-portal"
          >
            <ExternalLink className="w-3 h-3" /> Developer Portal
          </Link>
        </div>
      </div>

      <div className="border-b border-border/40 mb-6 flex items-center gap-1">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-xs font-medium -mb-px border-b-2 transition-colors inline-flex items-center gap-1.5 ${
              tab === t.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`platform-admin-tab-${t.key}`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Real-time health strip — surfaces anything an admin needs to react to */}
      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6"
        data-testid="platform-admin-health-strip"
      >
        <HealthCard
          icon={Layers}
          label="Active workspaces"
          value={healthLoading ? '…' : (health?.active_workspaces ?? 0).toLocaleString()}
          tone="neutral"
          testid="health-active-workspaces"
        />
        <HealthCard
          icon={Key}
          label="Active API keys"
          value={healthLoading ? '…' : (health?.active_api_keys ?? 0).toLocaleString()}
          tone="neutral"
          testid="health-active-api-keys"
        />
        <HealthCard
          icon={AlertTriangle}
          label="Budget rejections · 24h"
          value={healthLoading ? '…' : (health?.budget_exhausted_24h ?? 0).toLocaleString()}
          tone={(health?.budget_exhausted_24h ?? 0) > 0 ? 'warn' : 'neutral'}
          testid="health-budget-rejections"
        />
        <HealthCard
          icon={TrendingUp}
          label="Top workspace today"
          value={healthLoading
            ? '…'
            : (health?.top_workspace_today
                ? `${health.top_workspace_today.name} · $${(health.top_workspace_today.cost_usd || 0).toFixed(4)}`
                : '—')}
          tone="neutral"
          testid="health-top-workspace"
        />
      </div>

      <Active />
    </div>
  );
}
